import { useState, useEffect } from 'react'
import { FloppyDiskIcon, ArrowCounterClockwiseIcon } from '@phosphor-icons/react'
import {
    ScoopButton,
    ScoopText,
    ScoopSpinner,
    ScoopRow,
    ScoopCenter,
    ScoopColumn,
} from '../../../../ui'
import styles from './JsonEditor.module.scss'

export const JsonEditor = ({
    filePath,
    content,
    isLoading,
    onSave,
    isSaving,
    onDirtyStateChange,
}) => {
    const [editedContent, setEditedContent] = useState('')
    const [parseError, setParseError] = useState(null)
    const [hasChanges, setHasChanges] = useState(false)

    useEffect(() => {
        if (content !== null) {
            const formatted = JSON.stringify(content, null, 2)
            setEditedContent(formatted)
            setHasChanges(false)
            setParseError(null)
        }
    }, [content])

    useEffect(() => {
        onDirtyStateChange?.(hasChanges)
    }, [hasChanges, onDirtyStateChange])

    const handleChange = (e) => {
        const newValue = e.target.value
        setEditedContent(newValue)
        setHasChanges(true)

        // Validate JSON
        try {
            JSON.parse(newValue)
            setParseError(null)
        } catch (err) {
            setParseError(err.message)
        }
    }

    const handleSave = () => {
        if (parseError) return

        try {
            const parsed = JSON.parse(editedContent)
            onSave(parsed)
            setHasChanges(false)
        } catch (err) {
            setParseError(err.message)
        }
    }

    const handleReset = () => {
        if (content !== null) {
            const formatted = JSON.stringify(content, null, 2)
            setEditedContent(formatted)
            setHasChanges(false)
            setParseError(null)
        }
    }

    if (!filePath) {
        return (
            <ScoopCenter className={styles.emptyState}>
                <ScoopColumn className={styles.emptyContent}>
                    <ScoopText className={styles.emptyTitle}>No file selected</ScoopText>
                    <ScoopText className={styles.emptyDescription}>
                        Select a file from the list to view and edit its contents
                    </ScoopText>
                </ScoopColumn>
            </ScoopCenter>
        )
    }

    if (isLoading) {
        return (
            <ScoopCenter className={styles.loadingState}>
                <ScoopSpinner size={32} />
            </ScoopCenter>
        )
    }

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <ScoopRow className={styles.headerContent}>
                    <ScoopText className={styles.filePath}>{filePath}</ScoopText>
                    <ScoopRow className={styles.actions}>
                        {hasChanges && (
                            <ScoopButton
                                variant="grey"
                                size="sm"
                                onClick={handleReset}
                                disabled={isSaving}
                                leftSection={<ArrowCounterClockwiseIcon size={16} />}
                            >
                                Reset
                            </ScoopButton>
                        )}
                        <ScoopButton
                            variant="default"
                            size="sm"
                            onClick={handleSave}
                            disabled={!hasChanges || parseError || isSaving}
                            loading={isSaving}
                            leftSection={<FloppyDiskIcon size={16} />}
                        >
                            Save
                        </ScoopButton>
                    </ScoopRow>
                </ScoopRow>
                {parseError && (
                    <div className={styles.errorBanner}>
                        <ScoopText className={styles.errorText}>
                            Invalid JSON: {parseError}
                        </ScoopText>
                    </div>
                )}
            </div>
            <div className={styles.editorWrapper}>
                <textarea
                    className={styles.editor}
                    value={editedContent}
                    onChange={handleChange}
                    spellCheck={false}
                    disabled={isSaving}
                />
            </div>
        </div>
    )
}
